import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarUsuarios,
  crearUsuario,
  actualizarUsuario,
  eliminarUsuario,
  desbloquearUsuario,
} from "../../../../services/usuariosService";
import { listarRoles } from "../../../../services/rolesService";
import { listarCentros } from "../../../../services/centrosService";

const initialFormState = {
  nombre_completo: "",
  documento_identidad: "",
  telefono: "",
  email: "",
  rol_id: "",
  centro_id: "",
  activo: true,
  password: "",
};

const normalizeUsuario = (usuario = {}) => ({
  ...usuario,
  activo:
    typeof usuario.activo === "boolean"
      ? usuario.activo
      : usuario.activo === 1 || usuario.activo === "1",
});

const sanitizeText = (value = "") =>
  typeof value === "string"
    ? value.trim()
    : value !== undefined && value !== null
      ? String(value).trim()
      : "";

const sanitizeDocumento = (value = "") =>
  sanitizeText(value).replace(/\D/g, "");

const generateUsername = (nombreCompleto = "", documento = "", fallback = "") => {
  if (fallback) return fallback;
  const base =
    sanitizeText(nombreCompleto).split(/\s+/)[0]?.toLowerCase() || "usuario";
  const doc = sanitizeDocumento(documento);
  const suffix = doc ? doc.slice(-2) : String(Math.floor(Math.random() * 90 + 10));
  return `${base}${suffix}`;
};

const validarUsuario = (form, { requirePassword = false } = {}) => {
  if (!sanitizeText(form.nombre_completo)) return "El nombre es obligatorio.";
  const documento = sanitizeDocumento(form.documento_identidad);
  if (!documento) return "El documento es obligatorio.";
  if (documento.length !== 10)
    return "El documento debe tener 10 digitos.";
  if (!sanitizeText(form.email)) return "El correo es obligatorio.";
  if (!form.rol_id) return "Selecciona un rol.";
  if (requirePassword && !sanitizeText(form.password))
    return "Ingresa una contrasena temporal.";
  return null;
};

const buildPayload = (
  form,
  fallbackUsername,
  { requirePassword = false } = {}
) => {
  const documento = sanitizeDocumento(form.documento_identidad);
  const payload = {
    username: generateUsername(form.nombre_completo, documento, fallbackUsername),
    email: sanitizeText(form.email) || null,
    nombre_completo: sanitizeText(form.nombre_completo),
    documento_identidad: documento,
    telefono: sanitizeText(form.telefono) || null,
    rol_id: form.rol_id ? Number(form.rol_id) : null,
    centro_id: form.centro_id ? Number(form.centro_id) : null,
    activo: form.activo ? 1 : 0,
  };

  const password = sanitizeText(form.password);
  if (requirePassword && !password) {
    throw new Error("La contrasena temporal es obligatoria.");
  }
  if (password) payload.password = password;

  return payload;
};

export default function useUsuariosLogic() {
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [centrosLoading, setCentrosLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const [modo, setModo] = useState("");
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEdicion, setSavingEdicion] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [unlockingId, setUnlockingId] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [centerFilter, setCenterFilter] = useState("todos");

  const showAlert = useCallback(
    (type, message) => setAlerta({ type, message }),
    []
  );
  const dismissAlert = useCallback(() => setAlerta(null), []);

  const loadUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarUsuarios();
      setUsuarios((Array.isArray(data) ? data : []).map(normalizeUsuario));
      setListError(null);
    } catch (error) {
      console.error(error);
      setListError(error.message || "No se pudo cargar la lista de usuarios.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const data = await listarRoles();
      const rolesList = Array.isArray(data)
        ? data.map((rol) => ({
          id: rol.id,
          nombre: rol.nombre,
        }))
        : [];
      setRoles(rolesList);
    } catch (error) {
      console.error(error);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const loadCentros = useCallback(async () => {
    setCentrosLoading(true);
    try {
      const data = await listarCentros();
      setCentros(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      showAlert("warning", error.message || "No se pudo cargar la lista de centros.");
    } finally {
      setCentrosLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadUsuarios();
  }, [loadUsuarios]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    loadCentros();
  }, [loadCentros]);

  const resetForms = useCallback(() => {
    setFormNuevo({ ...initialFormState, activo: true });
    setFormEditar({ ...initialFormState, activo: true });
  }, []);

  const abrirModalNuevo = useCallback(() => {
    resetForms();
    setModo("nuevo");
    setUsuarioSeleccionado(null);
  }, [resetForms]);

  const abrirModalEditar = useCallback((usuario) => {
    if (!usuario) return;
    const normalized = normalizeUsuario(usuario);
    setUsuarioSeleccionado(normalized);
    setFormEditar({
      nombre_completo: normalized.nombre_completo || "",
      documento_identidad: normalized.documento_identidad || "",
      telefono: normalized.telefono || "",
      email: normalized.email || "",
      rol_id: normalized.rol_id ? String(normalized.rol_id) : "",
      centro_id: normalized.centro_id ? String(normalized.centro_id) : "",
      activo: normalized.activo,
      password: "",
    });
    setModo("editar");
  }, []);

  const abrirModalEliminar = useCallback((usuario) => {
    if (!usuario) return;
    setUsuarioSeleccionado(normalizeUsuario(usuario));
    setModo("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModo("");
    setUsuarioSeleccionado(null);
    resetForms();
  }, [resetForms]);

  const handleCrearUsuario = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const validation = validarUsuario(formNuevo, { requirePassword: true });
      if (validation) {
        showAlert("warning", validation);
        return;
      }

      setSavingNuevo(true);
      try {
        const payload = buildPayload(formNuevo, null, { requirePassword: true });
        const response = await crearUsuario(payload);
        const rolName =
          roles.find((rol) => rol.id === payload.rol_id)?.nombre || "";
        const { password, ...rest } = payload;
        const nuevo = normalizeUsuario({
          id: response?.id ?? Date.now(),
          ...rest,
          rol_nombre: rolName,
        });
        setUsuarios((prev) => [nuevo, ...prev]);
        showAlert("success", "Usuario creado correctamente.");
        cerrarModal();
      } catch (error) {
        console.error(error);
        showAlert("danger", error.message || "No se pudo crear el usuario.");
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, roles, cerrarModal, showAlert]
  );

  const handleActualizarUsuario = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!usuarioSeleccionado) return;
      const validation = validarUsuario(formEditar);
      if (validation) {
        showAlert("warning", validation);
        return;
      }

      setSavingEdicion(true);
      try {
        const payload = buildPayload(
          formEditar,
          usuarioSeleccionado.username
        );
        await actualizarUsuario(usuarioSeleccionado.id, payload);
        const rolName =
          roles.find((rol) => rol.id === payload.rol_id)?.nombre ||
          usuarioSeleccionado.rol_nombre ||
          "";
        const { password, ...rest } = payload;
        setUsuarios((prev) =>
          prev.map((usuario) =>
            usuario.id === usuarioSeleccionado.id
              ? normalizeUsuario({
                ...usuario,
                ...rest,
                rol_nombre: rolName,
              })
              : usuario
          )
        );
        showAlert("success", "Usuario actualizado correctamente.");
        cerrarModal();
      } catch (error) {
        console.error(error);
        showAlert(
          "danger",
          error.message || "No se pudo actualizar el usuario."
        );
      } finally {
        setSavingEdicion(false);
      }
    },
    [formEditar, usuarioSeleccionado, roles, cerrarModal, showAlert]
  );

  const handleEliminarUsuario = useCallback(async () => {
    if (!usuarioSeleccionado) return;
    const usuario = usuarioSeleccionado;
    setDeleting(true);
    try {
      await eliminarUsuario(usuario.id);
      setUsuarios((prev) => prev.filter((item) => item.id !== usuario.id));
      showAlert("success", "Usuario eliminado correctamente.");
      cerrarModal();
    } catch (error) {
      console.error(error);
      const backendLoggingError = String(error?.data?.error || "")
        .toLowerCase()
        .includes("username is not defined");
      if (backendLoggingError) {
        setUsuarios((prev) => prev.filter((item) => item.id !== usuario.id));
        showAlert("success", "Usuario eliminado correctamente.");
        cerrarModal();
      } else {
        showAlert("danger", error.message || "No se pudo eliminar el usuario.");
      }
    } finally {
      setDeleting(false);
    }
  }, [usuarioSeleccionado, cerrarModal, showAlert]);

  const handleDesbloquearUsuario = useCallback(
    async (usuario) => {
      if (!usuario?.id) return;
      setUnlockingId(usuario.id);
      try {
        await desbloquearUsuario(usuario.id);
        setUsuarios((prev) =>
          prev.map((item) =>
            item.id === usuario.id
              ? { ...item, intentos_fallidos: 0, bloqueado_hasta: null }
              : item
          )
        );
        showAlert("success", "Usuario desbloqueado correctamente.");
      } catch (error) {
        console.error(error);
        showAlert("danger", error.message || "No se pudo desbloquear el usuario.");
      } finally {
        setUnlockingId(null);
      }
    },
    [showAlert]
  );

  const resumen = useMemo(() => {
    const total = usuarios.length;
    let activos = 0;
    let inactivos = 0;
    let sinRol = 0;
    let sinCentro = 0;

    usuarios.forEach((usuario) => {
      if (usuario.activo) activos += 1;
      else inactivos += 1;

      if (!usuario.rol_id) sinRol += 1;
      if (!usuario.centro_id) sinCentro += 1;
    });

    return {
      total,
      activos,
      inactivos,
      sinRol,
      sinCentro,
    };
  }, [usuarios]);

  const aplicarKpiFiltro = useCallback((key) => {
    switch (key) {
      case "activos":
        setStatusFilter("activos");
        setRoleFilter("todos");
        setCenterFilter("todos");
        break;
      case "inactivos":
        setStatusFilter("inactivos");
        setRoleFilter("todos");
        setCenterFilter("todos");
        break;
      case "sinRol":
        setRoleFilter("sin-rol");
        setStatusFilter("todos");
        setCenterFilter("todos");
        break;
      case "sinCentro":
        setCenterFilter("sin-centro");
        setStatusFilter("todos");
        setRoleFilter("todos");
        break;
      default:
        setStatusFilter("todos");
        setRoleFilter("todos");
        setCenterFilter("todos");
        break;
    }
  }, []);

  const limpiarFiltros = useCallback(() => {
    setSearch("");
    setRoleFilter("todos");
    setStatusFilter("todos");
    setCenterFilter("todos");
  }, []);

  const activeQuickFilter =
    statusFilter === "activos"
      ? "activos"
      : statusFilter === "inactivos"
        ? "inactivos"
        : roleFilter === "sin-rol"
          ? "sinRol"
          : centerFilter === "sin-centro"
            ? "sinCentro"
            : "total";

  const filteredUsuarios = useMemo(() => {
    const term = sanitizeText(search).toLowerCase();
    return usuarios.filter((usuario) => {
      const matchesSearch =
        !term ||
        [usuario.nombre_completo, usuario.email, usuario.documento_identidad]
          .filter((field) => field !== undefined && field !== null)
          .some((field) => String(field).toLowerCase().includes(term));
      const matchesRole =
        roleFilter === "todos" ||
        (roleFilter === "sin-rol" && !usuario.rol_id) ||
        String(usuario.rol_id) === roleFilter;
      const matchesStatus =
        statusFilter === "todos" ||
        (statusFilter === "activos" && usuario.activo) ||
        (statusFilter === "inactivos" && !usuario.activo);
      const matchesCenter =
        centerFilter === "todos" ||
        (centerFilter === "sin-centro" && !usuario.centro_id) ||
        String(usuario.centro_id) === centerFilter;
      return matchesSearch && matchesRole && matchesStatus && matchesCenter;
    });
  }, [usuarios, search, roleFilter, statusFilter, centerFilter]);

  const getSuggestedUsername = useCallback(
    (form, fallbackUsername) =>
      generateUsername(form?.nombre_completo, form?.documento_identidad, fallbackUsername),
    []
  );

  return {
    data: {
      usuarios,
      roles,
      centros,
      resumen,
      activeQuickFilter,
      filteredUsuarios,
    },
    state: {
      loading,
      listError,
      rolesLoading,
      centrosLoading,
    },
    alerts: {
      alerta,
      dismissAlert,
    },
    filters: {
      search,
      setSearch,
      roleFilter,
      setRoleFilter,
      statusFilter,
      setStatusFilter,
      centerFilter,
      setCenterFilter,
      limpiarFiltros,
    },
    forms: {
      nuevo: { values: formNuevo, setValues: setFormNuevo },
      editar: { values: formEditar, setValues: setFormEditar },
    },
    modal: {
      modo,
      usuarioSeleccionado,
      abrirModalNuevo,
      abrirModalEditar,
      abrirModalEliminar,
      cerrarModal,
      isNuevo: modo === "nuevo",
      isEditar: modo === "editar",
      isEliminar: modo === "eliminar",
    },
    saving: {
      savingNuevo,
      savingEdicion,
      deleting,
      unlockingId,
    },
    actions: {
      aplicarKpiFiltro,
      handleCrearUsuario,
      handleActualizarUsuario,
      handleEliminarUsuario,
      handleDesbloquearUsuario,
      loadUsuarios,
    },
    utils: {
      getSuggestedUsername,
    },
  };
}

export { initialFormState, generateUsername };
